<template>
  <div class="sui-popover-wrapper">
    <component
      :is="tag"
      ref="reference"
      class="sui-reference"
      :class="{ 'sui-reference--clickable': trigger === 'click' && !hasExternalControl }"
      @click="handleClick"
    >
      <slot name="title"></slot>
      <Teleport to="body" :disabled="!appendToBody">
        <div
          ref="popover"
          v-on-click-outside="[handleClickOutside, { ignore: ['.sui-reference--clickable'] }]"
          :class="[{ 'is-tooltip': tooltip }, popperExtraClass]"
          class="sui-popper font-body-s-400"
        >
          <slot name="body"></slot>
          <div
            id="arrow"
            class="sui-popper-arrow"
            :class="{
              'visible-arrow': visibleArrow,
              'narrow-arrow': isNeedAddClass,
            }"
            data-popper-arrow
          ></div>
        </div>
      </Teleport>
    </component>
  </div>
</template>

<script lang="ts" setup>
import {ref, computed, onMounted, onBeforeUnmount} from 'vue'
import { createPopper, Instance } from '@popperjs/core/lib/popper-lite.js'
import preventOverflow from '@popperjs/core/lib/modifiers/preventOverflow.js'
import flip from '@popperjs/core/lib/modifiers/flip.js'
import hide from '@popperjs/core/lib/modifiers/hide.js'
import arrow from '@popperjs/core/lib/modifiers/arrow.js'
import offsetModifier from '@popperjs/core/lib/modifiers/offset.js'
import { useEventBus } from '@vueuse/core'
import { vOnClickOutside } from '@vueuse/components'

const SHOW_POPPER = 'show-popper'
// const CONTAINER_SELECTOR = '.data-containers'
const bus = useEventBus<HTMLElement>('popover')

type Props = {
  disabled?: boolean,
  appendToBody?: boolean,
  tag?: 'span' | 'div',
  trigger?: 'hover' | 'click',
  visibleArrow?: boolean,
  placement: 'auto' | 'auto-start' | 'auto-end' | 'top' | 'top-start' | 'top-end' | 'bottom' |
  'bottom-start' | 'bottom-end' | 'right' | 'right-start' | 'right-end' | 'left' | 'left-start' | 'left-end' ,
  boundariesSelector?: string,
  tooltip: boolean,
  delay: number,
  offset: [number | null | undefined, number | null | undefined],
  stopPropagation: boolean,
  hasExternalControl?: boolean,
  modifiers?: string[],
  popperExtraClass?: string
}


const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  appendToBody: false,
  tag: 'span',
  trigger: 'hover',
  visibleArrow: false,
  placement: 'auto',
  boundariesSelector: '',
  tooltip: true,
  popperExtraClass: undefined,
  offset: () => [null, null],
  modifiers: () => ['preventOverflow', 'flip', 'hide', 'arrow', 'offset']
})

const emit = defineEmits(['show', 'hide'])

let timer: number | undefined
const reference = ref<null | HTMLElement>(null) //реф на дом ноду элемента
const popover = ref<null | HTMLElement>(null) // реф на дом ноду поппера
let boundary: HTMLElement | null = null // дом нода границ
let instance: Instance | null = null

const isNeedAddClass = computed(() => {
  // добавление класса, чтобы был узкий "язык"
  const placement = [
    'auto-start',
    'auto-end',
    'left-start',
    'left-end',
    'right-start',
    'right-end',
  ]
  return placement.includes(props.placement)
})

function handleClick(event: Event) {
  if (props.hasExternalControl) return
  if (props.stopPropagation) {
    event.stopPropagation()
    event.preventDefault()
  }
  const pp = popover.value as HTMLElement
  if (!pp || !(pp instanceof HTMLElement)) return
  if (pp.hasAttribute(SHOW_POPPER) && props.trigger !== 'click') {
    return
  }
  bus.emit(reference.value!)
}

function handleClickOutside() {
  const pp = popover.value as HTMLElement
  if (!pp || !(pp instanceof HTMLElement)) return
  if (pp.hasAttribute(SHOW_POPPER)) {
    hidePopper()
  }
}

function hideOtherPoppers(target: HTMLElement) {
  if (reference.value !== target && popover.value!.hasAttribute(SHOW_POPPER)) {
    hidePopper()
  }
}

function initBaseData() {
  boundary = document.querySelector(props.boundariesSelector || '.main-content')
}

function initModifiersOptions() {
  preventOverflow.options = {
    boundary: boundary as HTMLElement,
    // escapeWithReference: true,
    padding: 20,
  }

  flip.options = {
    fallbackPlacements: ['top', 'bottom', 'right', 'left'],
    boundary: boundary as HTMLElement,
  }

  offsetModifier.options = {
    offset: () => props.offset,
  }
}

function addListeners() {
  if (props.hasExternalControl) return
  if (props.trigger === 'click') {
    reference.value!.addEventListener('click', togglePopper)
    return
  }

  reference.value!.addEventListener('mouseenter', showPopper)
  reference.value!.addEventListener('mouseleave', hidePopper)
}

function createInstance() {
  popover.value!.setAttribute(SHOW_POPPER, '')
  initModifiersOptions()

  const modifiers = []
  if (props.modifiers.includes('preventOverflow')) {
    modifiers.push(preventOverflow)
  }
  if (props.modifiers.includes('flip')) {
    modifiers.push(flip)
  }
  if (props.modifiers.includes('hide')) {
    modifiers.push(hide)
  }
  if (props.modifiers.includes('arrow')) {
    modifiers.push(arrow)
  }
  if (props.modifiers.includes('offset')) {
    modifiers.push(offsetModifier)
  }

  instance = createPopper(reference.value as HTMLElement, popover.value as HTMLElement, {
    placement: props.placement,
    modifiers
  })
}
function destroyInstance() {
  if (!instance) {
    return
  }
  instance.destroy()
  instance = null
}

function showPopper() {
  if (props.disabled) return
  timer = setTimeout(() => {
    createInstance()
    clearTimeout(timer)
    emit('show')
  }, props.delay)
}

function hidePopper() {
  popover.value!.removeAttribute(SHOW_POPPER)
  destroyInstance()
  clearTimeout(timer)
  emit('hide')
}

function togglePopper(event: Event) {
    event.stopPropagation()
    if (props.disabled) return
    if (popover.value && popover.value.hasAttribute(SHOW_POPPER)) {
      hidePopper()
    } else {
      showPopper()
    }
}

function updatePopper() {
  if (instance) {
    instance.update()
  }
}

onMounted(() => {
  initBaseData()
  addListeners()
  bus.on(hideOtherPoppers)
})

onBeforeUnmount(() => {
  hidePopper()
})

defineExpose({
  showPopper, 
  hidePopper,
  updatePopper
})
</script>

<style lang="scss" scoped>
.sui-popper {
  padding: 0;
  background-color: var(--color-bg-primary);
  display: none;
  word-wrap: break-word;
  white-space: normal;
  color: var(--color-text-contrast);
  &.is-tooltip {
    color: #fff;
    background-color: var(--gray-bg);
    width: 218px;
    padding: 8px;
    border-radius: 4px;
  }
}

.sui-popper[show-popper] {
  display: block;
  z-index: 9999;
}

.sui-popper[data-popper-reference-hidden] {
  visibility: hidden !important;
}

.sui-popper[data-popper-placement^='top'] > #arrow {
  top: calc(100% - 4px);
  &:before {
    transform: rotate(180deg);
  }
}

.sui-popper[data-popper-placement^='bottom'] > #arrow {
  bottom: calc(100% - 4px);
}

.sui-popper[data-popper-placement^='left'] > #arrow {
  left: calc(100% - 8px);
  &.narrow-arrow {
    left: calc(100% - 4px);
  }
  &:before {
    transform: rotate(90deg);
  }
}

.sui-popper[data-popper-placement^='right'] > #arrow {
  right: 100%;
  &.narrow-arrow {
    right: calc(100% - 4px);
  }
  &:before {
    transform: rotate(-90deg);
    right: -5px;
  }
}

.sui-popper-arrow {
  display: none;

  &.visible-arrow {
    display: block;
  }
}

.sui-popper-arrow,
.sui-popper-arrow::before {
  position: absolute;
  width: 15px;
  height: 15px;
  z-index: -1;
}
.sui-popper-arrow {
  width: 24px;
}

.sui-popper-arrow::before {
  content: '';
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 0 12px 14px 12px;
  border-color: transparent transparent var(--gray-bg) transparent;
}

.sui-popper-arrow.narrow-arrow::before {
  border-width: 0 7px 14px 7px;
}

.sui-reference {
  min-height: 20px;
}
</style>
